import type { PhotographData } from "@qendo/profile/files";
import { useMemo, useState } from "react";
import { View } from "react-native";
import { Button } from "~/components/Button";
import { FileListingItem } from "~/components/form/gallery/FileListingItem";
import { type FileValue, ImagePickerModal, valueToArray } from "~/components/form/gallery/ImagePickerModal";
import { Only } from "~/components/permission/Only";
import { Flex, Gap, Padding, Rounded } from "~/constants/layout";
import { useGallery } from "~/providers/GalleryProvider";
import { useTheme } from "~/providers/ThemeProvider";

export type MediaPickerProps = {
    disabled?: boolean;
    value?: FileValue<true>;
    onSelectionChanged: (value: FileValue<true>) => void;
};

export const MediaPicker = ({ value, onSelectionChanged, disabled }: MediaPickerProps) => {
    const { background } = useTheme();
    const { media, findMedia } = useGallery();
    const selected = useMemo<FileValue<true>>(() => valueToArray(value), [value]);
    const [show, setShow] = useState(false);

    const files: PhotographData[] = useMemo(() => {
        return selected.map((id) => findMedia(id)).filter((v) => !!v);
    }, [selected, findMedia]);

    const allMedia = useMemo(
        () => (show ? [...files, ...media.filter(({ id }) => !selected.includes(id))] : []),
        [show, files, media, selected],
    );

    const hide = () => setShow(false);

    const selectFile = (values: FileValue<true>) => {
        onSelectionChanged(values);
        setShow(false);
    };

    const removeFile = (id: string) => {
        onSelectionChanged(selected.filter((val) => val !== id));
    };

    return (
        <>
            <Only when={files.length}>
                <View style={[Padding.horizontal_md, Padding.vertical_sm, Rounded.lg, Gap.md, background.secondary]}>
                    {files.map((item) => (
                        <FileListingItem key={item.id} item={item} onRequestRemove={removeFile} disabled={disabled} />
                    ))}
                </View>
            </Only>
            <View style={[Flex.row, Gap.sm]}>
                <Button
                    variant="secondary"
                    disabled={disabled}
                    title="Attach file"
                    icon="paperclip"
                    onPress={() => setShow(true)}
                />
            </View>
            <Only when={show}>
                <ImagePickerModal
                    value={selected}
                    items={allMedia}
                    onItemsSelected={selectFile}
                    onRequestClose={hide}
                    multiple
                    optional
                />
            </Only>
        </>
    );
};
